import { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui-kit/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { cn } from '@/lib/utils';

interface SearchableMultiSelectOption {
  value: string;
  label: string;
}

interface SearchableMultiSelectProps {
  options: SearchableMultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  /** Suffix of the trigger's count summary, e.g. "selected" -> "3 selected". */
  selectedText: string;
  /** Builds the accessible label of a chip's remove button. */
  removeLabel: (label: string) => string;
  disabled?: boolean;
  className?: string;
}

/**
 * Multi-select dropdown with a search box. The picked options are listed as removable chips under
 * the trigger, so a long selection stays readable without opening the list.
 */
export const SearchableMultiSelect = ({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  selectedText,
  removeLabel,
  disabled,
  className,
}: SearchableMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const toggle = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((item) => item !== optionValue)
        : [...value, optionValue]
    );
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setKeyword('');
  };

  const normalizedKeyword = keyword.trim().toLowerCase();
  const visibleOptions = normalizedKeyword
    ? options.filter((option) => option.label.toLowerCase().includes(normalizedKeyword))
    : options;
  const selectedOptions = value
    .map((selected) => options.find((option) => option.value === selected))
    .filter((option): option is SearchableMultiSelectOption => Boolean(option));

  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)}>
      {/* modal: keeps the list scrollable when this select is used inside a Dialog. */}
      <Popover open={open} onOpenChange={handleOpenChange} modal>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'h-9 w-full justify-between px-3 font-normal',
              value.length === 0 && 'text-muted-foreground'
            )}
          >
            <span className="truncate">
              {selectedOptions.length > 0
                ? `${selectedOptions.length} ${selectedText}`
                : placeholder}
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
          {/* Filtering is done above so it matches on the label only, never on the option id. */}
          <Command shouldFilter={false}>
            <CommandInput
              value={keyword}
              onValueChange={setKeyword}
              placeholder={searchPlaceholder}
            />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {visibleOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => toggle(option.value)}
                  >
                    <Check
                      className={cn(
                        'mr-2 size-4',
                        value.includes(option.value) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedOptions.length > 0 && (
        <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
          {selectedOptions.map((option) => (
            <Badge key={option.value} variant="outline" className="max-w-full gap-1">
              <span className="truncate">{option.label}</span>
              {!disabled && (
                <button
                  type="button"
                  className="shrink-0"
                  onClick={() => toggle(option.value)}
                  aria-label={removeLabel(option.label)}
                >
                  <X className="size-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
